import {
    usePropertyStreet1,
    usePropertyStreet2,
    usePropertyCity,
    usePropertyState,
    usePropertyZipCode,
} from '../../../../queries/usePropertyCoreFields';
import { useApplicationPropertiesCache } from './queries/useApplicationPropertiesCache';
import { PropertyAddressCity } from './components/PropertyAddressCity';
import { PropertyAddressState } from './components/PropertyAddressState';
import { PropertyAddressStreet1 } from './components/PropertyAddressStreet1';
import { PropertyAddressStreet2 } from './components/PropertyAddressStreet2';
import { PropertyAddressZipCode } from './components/PropertyAddressZipCode';
import { PropertyAddressCounty } from './components/PropertyAddressCounty';
import { ApplicationQuestionReviewItem } from '../../../components/ApplicationQuestionReviewItem';
import { ShowIf } from '../../../components/ShowIf';
import { CustomRule } from 'devextreme-react/validator';

export interface PropertyAddressProps {
    propertyGuid: string;
    required?: boolean;
    isReview?: boolean;
    readOnly?: boolean;
}

export const PropertyAddress: React.FC<PropertyAddressProps> = ({
    propertyGuid,
    required = false,
    readOnly = false,
    isReview = false,
}) => {
    const [propertyAddressState] = usePropertyState(propertyGuid);
    const [propertyAddressCity] = usePropertyCity(propertyGuid);
    const [propertyAddressLine1] = usePropertyStreet1(propertyGuid);
    const [propertyAddressLine2] = usePropertyStreet2(propertyGuid);
    const [propertyAddressZipCode] = usePropertyZipCode(propertyGuid);

    const currentProperties = useApplicationPropertiesCache();

    const fullAddress = () => {
        return (
            <>
                <div style={{ lineHeight: '1.5' }}>
                    {propertyAddressLine1}
                    <ShowIf showIf={propertyAddressLine2}>
                        <br />
                        {propertyAddressLine2}
                    </ShowIf>
                    <br />
                    {propertyAddressCity}, {propertyAddressState} {propertyAddressZipCode}
                </div>
            </>
        );
    };

    const lower = (value: any) => (value ?? '').toString().trim().toLowerCase();

    const customValidator = (
        <CustomRule
            message='Multiple properties have the same address'
            validationCallback={() => {
                const items = currentProperties?.items ?? [];
                const foundProperties = items.filter((p: any) => {
                    const address = p?.address ?? {};
                    return (
                        lower(address.street1) === lower(propertyAddressLine1) &&
                        lower(address.street2) === lower(propertyAddressLine2) &&
                        lower(address.city) === lower(propertyAddressCity) &&
                        lower(address.state) === lower(propertyAddressState) &&
                        lower(address.zipCode) === lower(propertyAddressZipCode)
                    );
                });
                if (foundProperties.length > 1) {
                    return false;
                } else {
                    return true;
                }
            }}
        />
    );

    return (
        <>
            {isReview ? (
                <>
                    <ApplicationQuestionReviewItem
                        label='Property Address'
                        value={fullAddress()}
                    />
                    <PropertyAddressCounty
                        propertyGuid={propertyGuid}
                        selectedState={propertyAddressState}
                        required={required}
                        isReview={true}
                    />
                </>
            ) : (
                <>
                    <div
                        className='pageSubHeader'
                        style={{ marginTop: 30 }}
                    >
                        Address
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <PropertyAddressStreet1
                            propertyGuid={propertyGuid}
                            required={required}
                            readOnly={readOnly}
                            validator={customValidator}
                        />
                        <PropertyAddressStreet2
                            propertyGuid={propertyGuid}
                            validator={customValidator}
                        />
                        <PropertyAddressCity
                            propertyGuid={propertyGuid}
                            required={required}
                            readOnly={readOnly}
                            validator={customValidator}
                        />
                        <PropertyAddressZipCode
                            propertyGuid={propertyGuid}
                            required={required}
                            readOnly={readOnly}
                            validator={customValidator}
                        />
                        <PropertyAddressState
                            propertyGuid={propertyGuid}
                            required={required}
                            readOnly={readOnly}
                            validator={customValidator}
                        />
                        <PropertyAddressCounty
                            propertyGuid={propertyGuid}
                            selectedState={propertyAddressState}
                            required={required}
                            readOnly={readOnly}
                        />
                    </div>
                </>
            )}
        </>
    );
};
